import { Link } from "react-router-dom";
import { TriangleAlert, Diamond, ArrowUpRight } from "lucide-react";
import {
  agentMap,
  phaseColorMap,
  priorityStripColor,
  taskStatusMeta,
} from "@/lib/mockData";
import { cn } from "@/lib/utils";

const MILESTONE_DOT = {
  done: "bg-emerald-500",
  at_risk: "bg-orange-500",
  upcoming: "bg-zinc-300",
};

export default function PhasesView({ roadmap }) {
  const { phases, tasks, milestones } = roadmap;

  return (
    <div data-testid="phases-view" className="flex flex-col gap-5">
      {phases.map((phase, idx) => {
        const phaseTasks = tasks.filter((t) => t.phaseId === phase.id);
        const phaseMilestones = milestones.filter(
          (m) => m.day >= phase.startDay && m.day <= phase.endDay,
        );
        const c = phaseColorMap[phase.color];
        const total = phaseTasks.length;
        const progress =
          total === 0
            ? 0
            : Math.round(
                phaseTasks.reduce((s, t) => s + (t.progress ?? 0), 0) / total,
              );

        return (
          <section
            key={phase.id}
            data-testid={`phases-view-card-${phase.id}`}
            className="card-soft overflow-hidden rounded-[28px]"
          >
            {/* Header */}
            <div
              className="flex items-start justify-between gap-4 px-6 pb-5 pt-6"
              style={{ borderTop: `4px solid ${c.solid}` }}
            >
              <div>
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{ color: c.solid }}
                >
                  Phase {idx + 1} · {phase.name}
                </div>
                <div className="mt-1 text-[20px] font-extrabold tracking-tight text-zinc-900">
                  {phase.weeks}
                </div>
                <div className="mt-0.5 text-[12.5px] text-zinc-500">
                  {phase.tagline}
                </div>
              </div>

              {phaseMilestones.length > 0 && (
                <div className="flex flex-col items-end gap-1">
                  <div className="eyebrow">Milestones</div>
                  <div className="flex flex-col items-end gap-1">
                    {phaseMilestones.map((m) => (
                      <span
                        key={m.id}
                        className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold text-zinc-700"
                        style={{
                          border: "1px solid rgba(0,0,0,0.05)",
                        }}
                      >
                        <Diamond
                          className={cn(
                            "h-2.5 w-2.5",
                            m.status === "done" && "fill-emerald-500 text-emerald-500",
                            m.status === "at_risk" && "fill-orange-500 text-orange-500 idea-dot-pulse",
                            m.status === "upcoming" && "text-zinc-400",
                          )}
                          strokeWidth={2}
                        />
                        Day {m.day}: {m.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Progress */}
            <div className="mb-5 flex items-center gap-3 px-6 text-[11px]">
              <span className="font-bold text-zinc-700">{progress}%</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${progress}%`, backgroundColor: c.solid }}
                />
              </div>
              <span className="text-zinc-400">{total} tasks</span>
            </div>

            {/* Task chips grid */}
            <div className="grid grid-cols-1 gap-2.5 px-6 pb-6 sm:grid-cols-2 xl:grid-cols-3">
              {phaseTasks.map((t) => {
                const isHuman = t.owner === "HUMAN";
                const ownerAgent = agentMap[t.owner];
                const status = taskStatusMeta[t.status] ?? taskStatusMeta.todo;
                const stripColor =
                  priorityStripColor[t.priority] ?? priorityStripColor.med;
                return (
                  <div
                    key={t.id}
                    data-testid={`phases-view-task-${t.id}`}
                    className="relative flex items-center gap-2.5 overflow-hidden rounded-2xl border border-black/[0.04] bg-white p-3"
                  >
                    <span
                      className="absolute left-0 top-0 h-full w-[3px]"
                      style={{ backgroundColor: stripColor }}
                      aria-hidden
                    />
                    <span
                      className="ml-1 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: status.dot }}
                    />
                    <span className="flex-1 truncate text-[12.5px] font-semibold text-zinc-900">
                      {t.title}
                    </span>
                    {isHuman ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-orange-700">
                        <TriangleAlert className="h-3 w-3" strokeWidth={2.6} />
                        Human
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-700">
                        {ownerAgent?.name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      <div className="text-right">
        <Link
          to="/tasks"
          className="inline-flex items-center gap-1 text-[12px] font-semibold text-zinc-600 hover:text-black"
        >
          Manage tasks
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
        </Link>
      </div>
    </div>
  );
}
